import {
  SECTION_FIELDS,
  WEEKLY_UPDATE_SECTIONS,
  WEEKLY_UPDATE_SECTION_LABELS,
  type ReportState,
  type UpdateLink,
  type UpdateSections,
} from '@mfos/shared';
import { Link } from 'react-router';
import { Badge } from '../../components/Badge';
import { humanize } from '../../lib/format';
import { REPORT_STATE_DISPLAY, REPORT_STATE_SHORT, linksBySection } from './update-display';
import { phrase } from '../../lib/i18n/runtime';

export function ReportStateBadge({
  state,
  compact = false,
}: {
  state: ReportState;
  compact?: boolean;
}) {
  const display = REPORT_STATE_DISPLAY[state];
  return <Badge tone={display.tone} label={compact ? REPORT_STATE_SHORT[state] : display.label} />;
}

/** A reported link: hidden items keep their place without a label (the reader may not see them). */
export function UpdateLinkItem({ link, workspaceId }: { link: UpdateLink; workspaceId: string }) {
  const kind = humanize(link.entityType);
  return (
    <li>
      {link.visible && link.link !== null ? (
        <Link to={`/w/${workspaceId}${link.link}`}>
          {kind}: {link.label ?? phrase('Untitled')}
        </Link>
      ) : (
        <span>
          {kind}: {link.label ?? '(not visible to you)'}
        </span>
      )}
      {link.currentStatus !== null && (
        <span className="meta"> · now {humanize(link.currentStatus)}</span>
      )}
      {link.note !== null && <span className="meta"> · {link.note}</span>}
    </li>
  );
}

/** Read-only rendering of all sections with their evidence links; empty sections are named. */
export function UpdateSectionsView({
  sections,
  links,
  workspaceId,
  headingLevel = 3,
}: {
  sections: UpdateSections;
  links: readonly UpdateLink[];
  workspaceId: string;
  headingLevel?: 2 | 3 | 4;
}) {
  const grouped = linksBySection(links);
  const Heading = `h${String(headingLevel)}` as 'h2' | 'h3' | 'h4';
  return (
    <div className="update-sections">
      {WEEKLY_UPDATE_SECTIONS.map((section) => {
        const text = sections[SECTION_FIELDS[section]];
        const sectionLinks = grouped.get(section) ?? [];
        return (
          <section key={section} className="update-section">
            <Heading>{phrase(WEEKLY_UPDATE_SECTION_LABELS[section])}</Heading>
            {text === null && sectionLinks.length === 0 ? (
              <p className="meta">{phrase('Nothing reported.')}</p>
            ) : (
              text !== null && <p className="prewrap">{text}</p>
            )}
            {sectionLinks.length > 0 && (
              <ul
                className="plain-list"
                aria-label={`${WEEKLY_UPDATE_SECTION_LABELS[section]} links`}
              >
                {sectionLinks.map((link) => (
                  <UpdateLinkItem
                    key={`${link.entityType}:${link.entityId}`}
                    link={link}
                    workspaceId={workspaceId}
                  />
                ))}
              </ul>
            )}
          </section>
        );
      })}
    </div>
  );
}
